import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { LegalLinks } from "@/components/LegalLinks";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) {
    navigate(user.role === "instructor" ? "/instructor" : "/games");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success(`Welcome back, ${userData.name}!`);
      navigate(userData.role === "instructor" ? "/instructor" : "/games");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("auth.invalidCredentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-bg relative">
      <div className="hero-overlay absolute inset-0" />
      
      {/* Language Switcher - top right */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher variant="login" />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/skyboard-logo.png" alt="SkyBoard" className="h-64 w-auto mx-auto mb-4" />
            <p className="text-slate-400 mt-2">{t("auth.loginSubtitle")}</p>
          </div>

          <Card className="border-0 shadow-2xl">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-heading">{t("auth.login")}</CardTitle>
              <CardDescription>{t("auth.loginSubtitle")}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t("auth.email")}</Label>
                  <Input id="email" type="email" placeholder="you@university.edu" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11" data-testid="login-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t("auth.password")}</Label>
                  <div className="relative">
                    <Input id="password" type={showPassword ? "text" : "password"} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11 pr-11" data-testid="login-password-input" />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                      data-testid="toggle-password-visibility-btn"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="login-submit-btn">
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t("common.loading")}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {t("auth.login")}
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
                <div className="text-center mt-3">
                  <a href="mailto:admin@skyboard.es" className="text-sm text-slate-400 hover:text-sky-500 transition-colors" data-testid="forgot-password-link">
                    {t("auth.forgotPassword")}
                  </a>
                </div>
              </form>
              <div className="mt-6 text-center text-sm text-slate-500">
                {t("auth.noAccount")}{" "}
                <Link to="/register" className="text-sky-600 hover:text-sky-700 font-medium" data-testid="register-link">
                  {t("auth.register")}
                </Link>
              </div>
            </CardContent>
          </Card>
          <LegalLinks />
        </div>
      </div>
    </div>
  );
}
